import React, { useRef, useState } from "react";
import {
  useDrag,
  useDrop,
  DndProvider,
  DragSourceMonitor,
  XYCoord,
} from "react-dnd";
import type { Identifier } from "dnd-core";
import { HTML5Backend } from "react-dnd-html5-backend";
import { FC, ReactNode } from "react";

interface DraggableProps {
  id: string;
  type: string;
  onDrop: (draggedId: number, droppedId: number) => void;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
  children: ReactNode;
  index: number;
}

interface DragDropContextProviderProps {
  children: ReactNode;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

export const Draggable: FC<DraggableProps> = ({
  id,
  children,
  index,
  moveCard,
  onDrop,
  type,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: type,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: type,
    item: () => {
      return { id, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item: DragItem, monitor: DragSourceMonitor) => {
      onDrop(Number(item.id), item.index);
    },
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));
  return (
    <div
      ref={ref}
      className="w-full p-2 cursor-move"
      style={{ opacity }}
      data-handler-id={handlerId}
    >
      {children}
    </div>
  );
};

// Drag and Drop Context Provider
const DragDropContextProvider: FC<DragDropContextProviderProps> = ({
  children,
}) => <DndProvider backend={HTML5Backend}>{children}</DndProvider>;

export { DragDropContextProvider };
